import {
  Permission,
  sandboxing,
  strings,
  WhitelistElementType,
  WhiteListURLComponentObject,
  WhitelistURLMetadata,
  WhitelistURLPermission
} from './whitelist-URL.datamodel';
import { Injectable, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ReefDatePipe } from '@reef/core';
import { map, take } from 'rxjs/operators';

import { ErrorHandlerService } from '../../core/services/errorHandler.service';
import { FilesService } from '../../shared/services/files.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AppendStarToPartialUrlPipe } from '../../shared/pipes/append-star-to-partial-url.pipe';
import { IncludesCaseInsensitivePipe } from '../../shared/pipes/includes-case-insensitive.pipe';
import { ASReefRESTService } from '../../core/rest.service';
import { HttpHeaders } from '@angular/common/http';
import * as WhitelistActions from './whitelist-URL.actions';
import * as WhiteListUrlSelectors from './whitelist-URL.selectors';
import { ActivationParametersService } from '../../activation-parameters/activation-parameters.service';
import { Parser } from '@json2csv/plainjs';

const drawbridgeToggleParamId = 'MYA_DRAWBRIDGE_A';
export const EXCEL_FORMULA = /((^|[,:;|\t\r])['"]?)([=\-+@][^"]*)/g;
export const PREPEND_TICK = "$1'$3";

/**
 * Removes "/" from the end of a string
 * @param url potentially ending with "/"
 * @return url without "/" at the end
 */
export function normalizeUrl(url: string): string {
  url = url.trim();
  while (url.endsWith("/")) {
    url = url.slice(0, -1);
  }
  return url;
}


/**
 * Sanitize a CSV field to prevent Excel from interpreting it as a formula
 * @param value the value to sanitize
 */
export function sanitizeCsvField(value: string): string {
  return value.replace(EXCEL_FORMULA, PREPEND_TICK);
}

const MEDIA_TYPE_TEXT_CSV = "text/csv";

/**
 * Whitelist url service
 */
@Injectable()
export class WhitelistURLService implements OnDestroy {
  whiteListPermissions$ = this.store.pipe(select(WhiteListUrlSelectors.selectWhiteListPermissions));
  whiteListMetadata$ = this.store.pipe(select(WhiteListUrlSelectors.selectWhiteListMetadata));
  urlResults$ = this.store.pipe(select(WhiteListUrlSelectors.selectURLResults));
  successMessage$ = this.store.pipe(select(WhiteListUrlSelectors.selectSuccessMessage));

  searchResults = new BehaviorSubject<Array<WhiteListURLComponentObject>>([]);
  searchResults$ = this.searchResults.asObservable();
  results: Array<WhiteListURLComponentObject> = [];
  readonly PARTIAL_URL_WILDCARD = '*';

  subscriptions: Subscription[] = [];

  constructor(
    private readonly store: Store,
    private readonly restService: ASReefRESTService,
    private readonly parameterService: ActivationParametersService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly datePipe: ReefDatePipe,
    private readonly filesService: FilesService
  ) {
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  setSuccessMessage(message = ''): void {
    this.store.dispatch(WhitelistActions.WhiteListSetSuccessMessage({message}));
  }

  /**
   * filter the results with the key and update the observable
   *
   * @param searchKey
   * @param results
   */
  launchQuickSearch(searchKey: string, results: any[]) {
    let searchResults;
    let url: string;
    const includesCaseInsensitive = new IncludesCaseInsensitivePipe();
    if (searchKey) {
      const searchKeyWithoutWildcard = searchKey.toLowerCase().slice(0, -1);
      searchResults = results.filter(
        whiteListUrlComponent => {
          url = whiteListUrlComponent.content.url;
          if (includesCaseInsensitive.transform(searchKey, url)) {
            return true;
          } else {
            return this.isPartialUrl(whiteListUrlComponent)
              && this.endsWithPartialUrlWildcard(searchKey)
              && includesCaseInsensitive.transform(searchKeyWithoutWildcard, url);
          }
        }
      );
    } else {
      searchResults = results;
    }
    this.searchResults.next(searchResults);
  }

  /**
   * Get the whitelist of URLs from Central Storage
   */
  getWhiteListURLs(): void {
    const path = "whitelistURLs";
    this.subscriptions.push(
      this.restService.get(path).subscribe(
        res => {
          this.parseWhitelistContents(res.components);
          this.getListCompleted(res);
        },
        errors => this.errorHandlerService.handleErrors(errors, path)
      )
    );
  }

  /**
   * Save a SS white list URL component to Central Storage
   *
   * @param newURLObject  the component to save
   */
  createWhiteListURL(newURLObject: WhiteListURLComponentObject): void {
    const path = "whitelistURLs";
    this.subscriptions.push(
      this.restService.post(path, newURLObject.content).subscribe(
        res => {
          this.parseWhitelistContents([res.component]);
          this.saveWhiteListURLCompleted(res, path);
        },
        errors => this.errorHandlerService.handleErrors(errors, path)
      )
    );
  }

  /**
   * Update a whitelist URL, send backend request
   *
   * @param editedObj
   * @param guid
   * @param searchKey
   */
  updateWhiteListURL(editedObj: WhiteListURLComponentObject, guid: string, searchKey: string): void {
    const content = editedObj.getContent();

    const path = `whitelistURLs/${guid}`;
    this.subscriptions.push(
      this.restService.put(path, content).subscribe(
        res => {
          this.parseWhitelistContents([res.component]);
          this.editWhiteListURLCompleted(res, path, searchKey);
        },
        errors => this.errorHandlerService.handleErrors(errors, path)
      )
    );
  }

  /**
   * Update a whitelist URL metadata, send backend request
   *
   * @param metadata
   * @param guid
   * @param searchKey
   */
  updateWhiteListURLMetadata(metadata: {key: string, value: string}[], guid: string, searchKey: string): void {
    const path = `whitelistURLs/${guid}/metadata`;
    this.subscriptions.push(
        this.restService.put(path, metadata).subscribe({
          next: () => {
            this.editWhiteListURLMetadataCompleted(metadata, guid, searchKey);
          },
          error: (errors: any) => this.errorHandlerService.handleErrors(errors, path)
        }));
  }


  /**
   * Retrieve whiteListPermissions, send backend request
   */
  retrieveWhiteListPermissions(): void {
    const path = "whitelistURLPermissions";
    this.subscriptions.push(
      this.restService.get(path).subscribe(
        res => this.retrieveWhiteListUrlPermissionsCompleted(res.permissions),
        errors => this.errorHandlerService.handleErrors(errors, path)
      )
    );
  }

  /**
   * Retrieve whiteList metadata, send backend request
   */
  retrieveWhiteListMetadata() {
    const path = "whitelistURLMetadata";
    this.subscriptions.push(
        this.restService.get(path).subscribe({
          next: res => this.store.dispatch(WhitelistActions.WhiteListMetadataRetrieved({
                whiteListUrlMetadata: res.metadata
              })),
          error: errors => this.errorHandlerService.handleErrors(errors, path)
        })
    );
  }

  /**
   * Call to delete action
   *
   * @param componentId
   * @param comment
   * @param searchKey
   */
  deleteWhiteListURL(componentId: string, comment: string, searchKey: string): void {
    const path = `whitelistURLs/${componentId}`;
    const headers = new HttpHeaders({"deletionReason": comment});
    this.subscriptions.push(
      this.restService.delete(path, headers)
        .subscribe(
          () => this.handleDelete(componentId, searchKey),
          errors => this.errorHandlerService.handleErrors(errors, path)
        )
    );
  }

  /**
   * Extract URLs into a CSV file.
   * Format:
   * URL, GUID, Comment, Created by, Created on, Script provider, Bypass sandboxing, Bypass sandboxing comment, Permissions, ...Metadata
   * @param metadataFields dynamic metadata fields to include in the header
   */
  extract(metadataFields: WhitelistURLMetadata[] = []): void {
    this.subscriptions.push(
      this.urlResults$
        .pipe(
          take(1),
          map(urls => (urls ?? []).map(this.toCsvExport(metadataFields))),
          map((x) => new Parser({}).parse(x)),
        )
        .subscribe((content) => {
          const blob = new Blob([content], {type: MEDIA_TYPE_TEXT_CSV});
          this.filesService.download(blob, "URL_whitelist_extract.csv");
          this.store.dispatch(WhitelistActions.WhiteListSetSuccessMessage({
            message: 'URLs were extracted.'
          }));
        })
    );
  }

  /**
   * Transform a WhiteListURLComponentObject to a dictionary for CSV export
   * @param url The url content to include in the CSV
   * @param metadataFields The metadata fields to include in the CSV
   * @private
   */
  private toCsvExport(metadataFields: WhitelistURLMetadata[]): (url: WhiteListURLComponentObject)=> {} {
    return url => Object.entries({
      ...this.exportDefaultColumns(url),
      ...this.exportPermissionColumns(url),
      ...this.exportMetadataColumns(url, metadataFields)
    })
      .map(([key, value]) => [key, this.encodeCSVEntry(value)])
      .reduce((acc, [key, value]) => ({...acc, [key]: value}), {});
  }

  /**
   * Build a dictionary of standard columns for CSV export
   * @param url
   * @private
   */
  private exportDefaultColumns(url: WhiteListURLComponentObject) {
    return {
      [strings.type]: url.content.elementType ? url.content.elementType : WhitelistElementType.FULL,
      [strings.url]: new AppendStarToPartialUrlPipe().transform(url),
      [strings.id]: url.id,
      [strings.comment]: url.content.comment || '',
      [strings.createdBy]: url.createdBy?.actorName ?? '',
      [strings.createdOn]: this.datePipe.transform(url.createdOn, 'ddMMMyy')?.toUpperCase() ?? '',
      [strings.scriptProvider]: url.content.provider ?? ''
    };
  }

  /**
   * Build a dictionary of metadata values for CSV export
   * @param url
   * @param metadataFields
   * @private
   */
  private exportMetadataColumns(url: WhiteListURLComponentObject, metadataFields: WhitelistURLMetadata[]): {[key: string]: string} {
    const metadataValues = (url.content.metadata ?? [])
      .reduce((acc, m) => acc.set(m.key, m.value), new Map<string, string>());
    return metadataFields.reduce((acc, item) => ({...acc, [item.id]: metadataValues.get(item.id) ?? ''}), {});
  }

  /**
   * Build a dictionary of permissions related values for CSV export
   * @param entry The entry to push the permissions values to
   * @param url The URL component to extract the permissions from
   * @private
   */
  private exportPermissionColumns(url: WhiteListURLComponentObject) {
    const sandboxingPermission = url.content.permissions.find(
      p => p.name === sandboxing
    );
    const displayablePermissions = url.content.permissions.filter(p => p.name !== sandboxing);
    if (sandboxingPermission?.linkedPermissionList) {
      sandboxingPermission.linkedPermissionList.forEach(
        (p: WhitelistURLPermission) => displayablePermissions.push(p));
    }
    const permissions = {
      [strings.permissions]: this.isPartialUrl(url)
        ? strings.nonApplicable
        : displayablePermissions
          .filter(p => p.value !== false)
          .map(p => p.name)
          .join(';')
    };
    if (sandboxingPermission) {
      return {
        [strings.sandboxing]: sandboxingPermission.value ? strings.no : strings.yes,
        [strings.bypassSandboxingComment]: sandboxingPermission.comment || '',
        ...permissions
      };
    } else if (url.content.url?.startsWith(strings.exePrefix) || this.isPartialUrl(url)) {
      return {
        [strings.sandboxing]: strings.nonApplicable,
        [strings.bypassSandboxingComment]: strings.nonApplicable,
        ...permissions
      };
    } else {
      return {
        [strings.sandboxing]: strings.notSet,
        [strings.bypassSandboxingComment]: '',
        ...permissions
      };
    }
  }

  /**
   * Automatic mapping between backend and UI permission objects
   * @param whiteListUrlPermissions
   */
  mapWhiteListUrlPermissionsToUI(whiteListUrlPermissions: WhitelistURLPermission[]): Permission[] {
    const permissions: Permission[] = [];
    whiteListUrlPermissions.forEach(whiteListUrlPermission => {
      permissions.push(this.mapSinglePermissionToUI(whiteListUrlPermission));
      if (whiteListUrlPermission.linkedPermissionList) {
        const linkedPermissions: Permission[] = [];
        whiteListUrlPermission.linkedPermissionList.forEach(whiteListLinkedPerm => {
          linkedPermissions.push(this.mapSinglePermissionToUI(whiteListLinkedPerm));
        });
        permissions[permissions.length - 1].linkedPermissionList = linkedPermissions;
      }
    });
    return permissions;
  }

  /**
   * Checks if the component URL is a PARTIAL or not
   * @param whitelistUrlComponent component to check
   * @return isPartialUrl boolean, true is PARTIAL
   */
  isPartialUrl(whitelistUrlComponent: WhiteListURLComponentObject): boolean {
    return whitelistUrlComponent.content.elementType === WhitelistElementType.PARTIAL;
  }

  /**
   * Checks if the string ends with PARTIAL_URL_WILDCARD
   * @param key the string
   * @return boolean true if ends with PARTIAL_URL_WILDCARD
   */
  private endsWithPartialUrlWildcard(key: string): boolean {
    return key.substring(key.length - 1) === this.PARTIAL_URL_WILDCARD;
  }

  /**
   * Map a backend permission to id, and label to be shown,
   * if the label is not found in the hardcoded map a new label will be generated using the same name as the backend one
   * @param whiteListPermission
   * @private
   */
  private mapSinglePermissionToUI(whiteListPermission: WhitelistURLPermission): Permission {
    return {
      label: whiteListPermission.text ?? '',
      id: whiteListPermission.text?.toLowerCase().replace(/\s/g, '-') ?? '',
      control: whiteListPermission.name,
      displayOnly: whiteListPermission.displayOnly
    };
  }

  /**
   * Encode and sanitize the string field for CSV export
   * @private
   * @param entry the string to encode
   */
  private encodeCSVEntry(entry: string): string {
    // Escape \n and \r. Need to be escaped for the browser (href)
    entry = entry.replace(/\n/g, "\\n").replace(/\r/g, "\\r");

    // Escape double quotes by doubling them
    entry = entry.replace(/"/g, "\"\"");

    // Fields containing line breaks (CRLF), double quotes, and commas are enclosed in double-quotes
    if (entry.includes(",") || entry.includes("\"") || entry.includes("\n") || entry.includes("\r")) {
      entry = `"${entry}"`;
    }

    // Escape any formula by prepending a single tick
    entry = sanitizeCsvField(entry);

    return entry;
  }

  /**
   * Convert the contents of a list of components from string to JSON objects
   * @param components the list of components
   * @private
   */
  private parseWhitelistContents(components: Array<any>) {
    if (components) {
      components.forEach(c => {
        c.content = typeof c.content === 'string'? JSON.parse(c.content) : c.content;
      });
    }
  }

  /**
   * Dispatch store's event to update the UI once results are returned
   *
   * @param components   the list of results
   */
  private getListCompleted({components}: any): void {
    this.store.dispatch(
      WhitelistActions.WhiteListRetrieved({
        components: this.filterExeUrls(components)
      })
    );
  }

  /**
   * Filters exe URLs if the feature is deactivated
   *
   * @param components
   * @private
   */
  private filterExeUrls(components: WhiteListURLComponentObject[]): WhiteListURLComponentObject[] {
    if (this.parameterService.isActive(drawbridgeToggleParamId)) {
      return components;
    }
    return components.filter(cmp => !cmp.content.url?.startsWith(strings.exePrefix));
  }

  /**
   * Dispatch store's event to update the results once the new URL component has been created
   *
   * @param component   the URL component that have been saved
   * @param errors      the errors returned if any
   * @param action action name for ErrorOnRequest action
   */
  private saveWhiteListURLCompleted(
    {component, errors}: any,
    action: string
  ): void {
    if (errors && errors.length > 0) {
      this.errorHandlerService.handleResponseErrors(errors, action);
    } else {
      this.store.dispatch(WhitelistActions.WhiteListURLSaved({component}));
    }
  }

  /**
   * Dispatch store's event to update the results once the new URL component has been updated
   *
   * @param component
   * @param errors
   * @param action
   * @param searchKey
   * @private
   */
  private editWhiteListURLCompleted(
    {component, errors}: any,
    action: string,
    searchKey: string): void {
    if (errors && errors.length > 0) {
      this.errorHandlerService.handleResponseErrors(errors, action);
    } else {
      this.store.dispatch(WhitelistActions.WhiteListUpdated({component}));
      this.launchQuickSearch(searchKey, this.results);
    }
  }

  /**
   * Dispatch store's event to update the results once the new URL component's metadata has been updated
   * @param metadata
   * @param guid
   * @param searchKey
   * @private
   */
  private editWhiteListURLMetadataCompleted(
      metadata: {key: string, value: string}[], guid: string, searchKey: string) {
    this.store.dispatch(WhitelistActions.WhiteListMetadataUpdated({metadata, guid}));
    this.launchQuickSearch(searchKey, this.results);
  }

  /**
   * Dispatch Store's event to set the allowed permissions for Whitelist components
   * @private
   * @param whiteListUrlPermissions
   */
  private retrieveWhiteListUrlPermissionsCompleted(whiteListUrlPermissions: WhitelistURLPermission[]) {
    this.store.dispatch(WhitelistActions.WhiteListPermissionsRetrieved({whiteListUrlPermissions}));
  }

  /**
   * Dispatch store's action to delete URL component with the given id
   *
   * @param componentId   Id of the component to be deleted
   * @param searchKey
   */
  private handleDelete(componentId: string, searchKey: string): void {
    this.store.dispatch(
      WhitelistActions.WhiteListDeleted({
          componentId
      })
    );
    this.launchQuickSearch(searchKey, this.results);
  }
}
